/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { subMinutes } from 'date-fns/subMinutes';
import {
  OccasionEvent,
  OccasionTermUnit,
} from 'uplisting-frontend/models/occasion';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  booking: ModelRegistry['booking'];
  scheduledMessage: ModelRegistry['scheduled-message'];
}

export interface BookingScheduledMessageSignature {
  Element: HTMLTableElement;

  Args: IArgs;
}

function termInMinutes(rule: ModelRegistry['automated-message-rule']): number {
  switch (rule.termUnit) {
    case OccasionTermUnit.minutes:
      return rule.term;
    case OccasionTermUnit.hours:
      return rule.term * 60;
    case OccasionTermUnit.days:
      return rule.term * 1440;
  }
}

export default class UiBookingScheduledMessageComponent extends Component<BookingScheduledMessageSignature> {
  @service notifications!: Services['notifications'];

  @cached
  get scheduledMessage(): ModelRegistry['scheduled-message'] {
    return this.args.scheduledMessage;
  }

  @cached
  get eventTime(): Date {
    const { booking } = this.args;
    const rule = this.scheduledMessage.automatedMessageRule;

    switch (rule.event) {
      case OccasionEvent.booked:
        return booking.bookedAt;
      case OccasionEvent.enquired:
        return booking.createdAt;
      case OccasionEvent.arrived:
        return booking.checkInDate;
      case OccasionEvent.departed:
        return booking.checkOutDate;
      case OccasionEvent.cancelled:
      case OccasionEvent.day_of_week:
        return subMinutes(
          this.scheduledMessage.scheduledFor,
          termInMinutes(rule),
        );
    }
  }

  @action
  @waitFor
  async handleToggleEnabled(): Promise<void> {
    const { scheduledMessage } = this;

    const enabled = scheduledMessage.enabled;

    scheduledMessage.enabled = !enabled;

    try {
      await scheduledMessage.save();

      this.notifications.info();
    } catch {
      scheduledMessage.enabled = enabled;

      this.notifications.error();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::ScheduledMessage': typeof UiBookingScheduledMessageComponent;
  }
}
